import React from "react";
import { Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { clean, money, moneyShort, share, toNumber, truncate } from "../format";
import { CategoryTooltip, SeriesTooltip } from "./primitives";

const MARGIN = { top: 8, right: 12, left: 4, bottom: 0 };
const LEGEND_STYLE = { fontSize: 12, paddingTop: 8 };

const axisProps = (palette) => ({
  stroke: palette.axis,
  tick: { fill: palette.axis, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: palette.grid },
});

const barCursor = (palette) => ({ fill: palette.grid, fillOpacity: 0.6 });

const Grid = ({ palette, vertical = false }) => (
  <CartesianGrid vertical={vertical} horizontal={!vertical} stroke={palette.grid} strokeWidth={1} />
);

const totalOf = (rows, key) => rows.reduce((sum, row) => sum + toNumber(row[key]), 0);

/* ------------------------------------------------------------------ summaries
 * Plain-language text read out instead of the SVG. */

export const trendSummary = (trend) =>
  `Sales billed ${money(totalOf(trend, "sales"))} and cash collected ${money(totalOf(trend, "collection"))} across ${trend.length} periods.`;

export const deliverySummary = (trend) =>
  `Delivered ${money(totalOf(trend, "delivered"))}, pending ${money(totalOf(trend, "pending"))}.`;

export const demandSummary = (trend) =>
  `Demanded ${money(totalOf(trend, "demanded"))}, invoiced ${money(totalOf(trend, "invoiced"))}, still open ${money(totalOf(trend, "open"))}.`;

export const rankedSummary = (rows, total) =>
  rows
    .slice(0, 5)
    .map((row) => `${clean(row.name)} ${money(row.value)}${total > 0 ? ` (${Math.round(share(row.value, total))}%)` : ""}`)
    .join("; ");

/* --------------------------------------------------------------------- charts */

export const TrendChart = ({ data, palette, height }) => {
  const axis = axisProps(palette);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={MARGIN}>
        <defs>
          <linearGradient id="dash-fill-sales" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.sales} stopOpacity={0.24} />
            <stop offset="100%" stopColor={palette.sales} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <Grid palette={palette} />
        <XAxis dataKey="label" {...axis} interval="preserveStartEnd" minTickGap={16} />
        <YAxis {...axis} width={62} tickFormatter={moneyShort} />
        <Tooltip cursor={{ stroke: palette.axis, strokeWidth: 1 }} content={<SeriesTooltip />} />
        <Legend iconType="plainline" wrapperStyle={LEGEND_STYLE} />
        <Area
          type="monotone"
          dataKey="sales"
          name="Sales billed"
          stroke={palette.sales}
          strokeWidth={2}
          fill="url(#dash-fill-sales)"
          dot={false}
          activeDot={{ r: 5, strokeWidth: 2, stroke: palette.surface }}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="collection"
          name="Cash collected"
          stroke={palette.collection}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 5, strokeWidth: 2, stroke: palette.surface }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export const DeliveryChart = ({ data, palette, height }) => {
  const axis = axisProps(palette);
  // A 2px surface stroke keeps a visible gap between the stacked fills.
  const segment = { stackId: "value", stroke: palette.surface, strokeWidth: 2, maxBarSize: 34, isAnimationActive: false };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={MARGIN}>
        <Grid palette={palette} />
        <XAxis dataKey="label" {...axis} minTickGap={12} />
        <YAxis {...axis} width={62} tickFormatter={moneyShort} />
        <Tooltip cursor={barCursor(palette)} content={<SeriesTooltip />} />
        <Legend iconType="rect" wrapperStyle={LEGEND_STYLE} />
        <Bar dataKey="delivered" name="Delivered" fill={palette.sales} {...segment} />
        <Bar dataKey="pending" name="Pending" fill={palette.pending} radius={[4, 4, 0, 0]} {...segment} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/** Demanded value split into what has been invoiced and what is still open. */
export const DemandChart = ({ data, palette, height }) => {
  const axis = axisProps(palette);
  const segment = { stackId: "value", stroke: palette.surface, strokeWidth: 2, maxBarSize: 34, isAnimationActive: false };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={MARGIN}>
        <Grid palette={palette} />
        <XAxis dataKey="label" {...axis} minTickGap={12} />
        <YAxis {...axis} width={62} tickFormatter={moneyShort} />
        <Tooltip cursor={barCursor(palette)} content={<SeriesTooltip />} />
        <Legend iconType="rect" wrapperStyle={LEGEND_STYLE} />
        <Bar dataKey="invoiced" name="Invoiced" fill={palette.collection} {...segment} />
        <Bar dataKey="open" name="Open" fill={palette.pending} radius={[4, 4, 0, 0]} {...segment} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const AgeingChart = ({ data, palette, height }) => {
  const axis = axisProps(palette);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={MARGIN}>
        <Grid palette={palette} />
        <XAxis dataKey="name" {...axis} />
        <YAxis {...axis} width={62} tickFormatter={moneyShort} />
        <Tooltip cursor={barCursor(palette)} content={<CategoryTooltip />} />
        <Bar dataKey="value" name="Outstanding" maxBarSize={48} radius={[4, 4, 0, 0]} isAnimationActive={false}>
          {data.map((row, index) => (
            <Cell key={row.name} fill={palette.ageing[Math.min(index, palette.ageing.length - 1)]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/** Horizontal ranking with value labels - products, regions, areas. */
export const RankedBarChart = ({ data, total, palette, height, labelWidth = 150, labelChars = 22, seriesName = "Sales value", color }) => {
  const axis = axisProps(palette);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart layout="vertical" data={data} margin={{ top: 4, right: 64, left: 4, bottom: 0 }}>
        <Grid palette={palette} vertical />
        <XAxis type="number" {...axis} tickFormatter={moneyShort} />
        <YAxis
          type="category"
          dataKey="name"
          {...axis}
          width={labelWidth}
          tickFormatter={(value) => truncate(clean(value), labelChars)}
        />
        <Tooltip cursor={barCursor(palette)} content={<CategoryTooltip total={total} />} />
        <Bar
          dataKey="value"
          name={seriesName}
          fill={color || palette.sales}
          maxBarSize={18}
          radius={[0, 4, 4, 0]}
          label={{ position: "right", fill: palette.axis, fontSize: 11, formatter: moneyShort }}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
